import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { costLabel, sessionDurationLabel, tokensLabel } from '@/lib/session-labels';
import { cn } from '@/lib/utils';
import { type SessionDetail, sessionFigures } from '@matter_extractor/shared';

/**
 * The eight figures at the head of the parcours (§12.2): durée, pages, tours,
 * coût, jetons, objectifs réussis, incidents, tours malformés.
 *
 * ## What it does NOT hold
 *
 * The breakdown per seat. §12.2 says so by name - « le détail par siège n'est
 * pas là : il vit dans la couche Appels de §12.3, parce que c'est une question
 * de réglage, qu'on se pose sur un tour et pas sur une session ». Eight tiles is
 * already the top of what a row can carry before it stops being readable.
 *
 * ## Two tiles turn yellow, and only two
 *
 * Incidents and malformed turns are DEFECTS of the dispositif rather than
 * measurements of the conversation, and the grill words them together with the
 * transcript's rule: « un défaut qui dort dans un accordéon n'est pas signalé ».
 * They go to `jaune-deep` as soon as they are not zero, which is also the only
 * value yellow may carry text at (charte).
 *
 * ## A session with no turn shows a dash, never a zero
 *
 * The rule the maquette states for the list, and it holds harder here: « zéro
 * euro est une mesure, l'absence de conversation n'en est pas une ». So the
 * cost and the tokens of a session that played nothing are a dash, while the
 * turns themselves are a true zero - that one IS a count.
 */

export interface SessionTilesProps {
  readonly detail: SessionDetail;
}

interface Tile {
  readonly label: string;
  readonly value: string;
  /** The smaller half of the value - « min », « € », « / 6 ». */
  readonly unit: string | null;
  readonly warn: boolean;
}

export function SessionTiles({ detail }: SessionTilesProps) {
  const figures = sessionFigures(detail);
  const played = figures.turnCount > 0;

  const tiles: readonly Tile[] = [
    {
      label: 'Durée',
      value: sessionDurationLabel(figures.durationMs),
      unit: null,
      warn: false,
    },
    { label: 'Pages', value: String(figures.pageCount), unit: null, warn: false },
    { label: 'Tours', value: String(figures.turnCount), unit: null, warn: false },
    { label: 'Coût', value: played ? costLabel(figures.costEur) : '-', unit: null, warn: false },
    { label: 'Jetons', value: played ? tokensLabel(figures.tokens) : '-', unit: null, warn: false },
    {
      label: 'Objectifs',
      value: String(figures.objectivesSucceeded),
      unit: `/ ${figures.objectivesTotal}`,
      warn: false,
    },
    {
      label: 'Incidents',
      value: String(figures.incidents),
      unit: null,
      warn: figures.incidents > 0,
    },
    {
      label: 'Malformés',
      value: String(figures.malformed),
      unit: null,
      warn: figures.malformed > 0,
    },
  ];

  return (
    // A description LIST and not a row of divs: eight tiles are eight
    // label-value pairs, which is what `dl` is for, and it is the one markup
    // where the name of a figure and the figure are tied together without a
    // `role` nobody would otherwise have to write. The maquette's own
    // `aria-label` then lands on an element that exposes it.
    <dl
      data-testid={SESSIONS_TEST_IDS.tiles}
      aria-label="Chiffres de la session"
      // Two columns on a phone, eight from `lg`: the maquette folds at 900 px,
      // and `admin-responsive.spec.ts` holds every admin screen at zero overflow
      // on a 390 px window.
      className="grid grid-cols-2 overflow-hidden rounded-md border-gray-200 border-r border-b sm:grid-cols-4 lg:grid-cols-8"
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          data-tile={tile.label}
          className="flex min-w-0 flex-col gap-px border-gray-200 border-t border-l bg-gray-50 px-3 py-2"
        >
          <dt className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
            {tile.label}
          </dt>
          <dd
            className={cn(
              'whitespace-nowrap font-semibold text-h4 tabular-nums',
              tile.warn ? 'text-jaune-deep' : 'text-gray-900',
            )}
          >
            {tile.value}
            {tile.unit === null ? null : (
              <span className="ml-1 font-normal text-caption text-gray-600">{tile.unit}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
